/* eslint @typescript-eslint/no-duplicate-enum-values: off */

export const dreamTeamSessionName = 'Dream team session';

export const teamDiscoveryActionPlanLink =
    '/Assets/Design/TeamDynamicsActionPlan.pdf';

export type TeamDiscoveryActionType = 'strategic' | 'team';

export type TeamDiscoveryActionSubtype = 'behavior' | 'cultural';

export enum AgreementItemScore {
    DoNotPursue = 0,
    Pursue = 1
}

export enum StrategyQuadrant {
    Cultivating,
    Exploring,
    Producing,
    Stabilizing
}
export enum DerivedTeamName {
    Unknown,
    Cultivating,
    Exploring,
    Producing,
    Stabilizing,
    Anchoring,
    Bolstering,
    Executing,
    Pathfinding,
    Adapting,
    CultivatingAndProducing,
    ExploringAndStabilizing
}

export const TeamTypesWithActions = [
    DerivedTeamName.Cultivating,
    DerivedTeamName.Exploring,
    DerivedTeamName.Producing,
    DerivedTeamName.Stabilizing,
    DerivedTeamName.Anchoring,
    DerivedTeamName.Bolstering,
    DerivedTeamName.Executing,
    DerivedTeamName.Pathfinding,
    DerivedTeamName.Adapting
];

export type PizzaConfigType = 'strat' | 'team';

export type BlindspotOrStrengthType = 'blindspot' | 'strength';

export enum StrategyQuadrantEmphasis {
    None = 0,
    Low,
    Medium,
    High
}

// A enum that defines the type of dream team session team objective type
export enum PizzaObjectiveType {
    Unknown = 0,
    Leadership,
    Standard
}

// Hardcoded map of questions ids that correspond to each quadrant
// WMC - we should really have the strat quadrant as a property of the item
// and included as part of the survey def that is grabbed from the server
// this is a hack for now that will be prone to errors.
export const strategyQuadrantQuestionMap: Readonly<
    Map<StrategyQuadrant, string[]>
> = Object.freeze(
    new Map([
        [StrategyQuadrant.Exploring, ['1', '4', '10', '12', '19']],
        [StrategyQuadrant.Cultivating, ['6', '8', '11', '16', '20']],
        [StrategyQuadrant.Stabilizing, ['2', '7', '9', '13', '18']],
        [StrategyQuadrant.Producing, ['3', '5', '14', '15', '17']]
    ])
);

export type StrategyQuadrantAlignmentMap = Map<
    StrategyQuadrant,
    {
        displayValue: string;
        emphasis: StrategyQuadrantEmphasis;
    }
>;

export type PizzaQuadrantScores = Map<StrategyQuadrant, number>;

export enum ConfidenceStrategyItemScore {
    NotSelected = null,
    LessConfident = 0,
    SomewhatConfident = 1,
    HighlyConfident = 2
}

export type AgreementStrategyItem = {
    id: string;
    label: string;
    scores: Map<AgreementItemScore, number>;
};

export type ConfidenceStrategyItem = {
    label: string;
    scores: Map<ConfidenceStrategyItemScore, number>;
};

export type StrategyTeamPlayGroundSelections = {
    // this is a typo but it'd be expensive to change so we're going to leave it
    selectedSrategies: string;
    selectedPeople: string;
};

export type PlaygroundSelectionsToSumoLogic = {
    selectedStrategies: string;
    selectedPeople: string;
    companyId: string;
    userId: string;
    page: string;
};

export type TeamConfigResult = {
    quadrants: StrategyQuadrant[];
    teamName: DerivedTeamName;
};

export type BehavioralEmphasisScores = Map<
    StrategyQuadrant,
    StrategyQuadrantEmphasis
>;

export type BehavioralEmphasisRanks = {
    A: number;
    B: number;
    C: number;
    D: number;
};

export type BehavioralQuadrantRanks = {
    [key in StrategyQuadrantNames]: number;
};

export type StrategyQuadrantNames =
    | 'Cultivating'
    | 'Exploring'
    | 'Producing'
    | 'Stabilizing';

export const Quadrants = [
    StrategyQuadrant.Cultivating,
    StrategyQuadrant.Exploring,
    StrategyQuadrant.Stabilizing,
    StrategyQuadrant.Producing
];

export type ActionTip = {
    id: string;
    title: string;
    description: string;
    isSelected: boolean;
    extraTitle?: string;
};

export type SelectedActivities = {
    pageName: string;
    selections: boolean[];
};

export type PizzaActivities = {
    count: number;
    pageName: string;
    state: SelectedActivities;
    collection: ActionTip[];
};
export interface IStrategyOrTeamSelection {
    readonly id: string;
    readonly text: string;
    readonly translationKey: string;
    readonly objectives: string;
    readonly keyResultsOrStake: string;
    readonly whoResponsible: string;
}

// This doesn't really make sense here but it's the only way to make the deps work
export class StrategyOrTeamSelection implements IStrategyOrTeamSelection {
    readonly id: string;
    readonly text: string;
    readonly translationKey: string;
    readonly objectives: string;
    readonly keyResultsOrStake: string;
    readonly whoResponsible: string;

    constructor(args: Partial<StrategyOrTeamSelection>) {
        Object.assign(this, args);
    }

    static mapIndexToItemId(index: string | number) {
        if (typeof index === 'string') {
            return parseInt(index, 10) + 1;
        } else return index + 1;
    }

    static mapItemIdToIndex(index: string | number) {
        if (typeof index === 'string') {
            return parseInt(index, 10) - 1;
        } else return index - 1;
    }
}

export const derivedTeamPercentageMap: Readonly<Map<DerivedTeamName, number>> =
    Object.freeze(
        // These numbers are hard-coded for a quick win,
        // and are computed using engagement data by the science team.
        // We may change them into live data in the future.
        new Map([
            [DerivedTeamName.Adapting, 15],
            [DerivedTeamName.Anchoring, 15],
            [DerivedTeamName.Bolstering, 14],
            [DerivedTeamName.Cultivating, 12],
            [DerivedTeamName.Executing, 11],
            [DerivedTeamName.Exploring, 11],
            [DerivedTeamName.Pathfinding, 9],
            [DerivedTeamName.Producing, 8],
            [DerivedTeamName.Stabilizing, 6]
        ])
    );

export const derivedTeamStrategyPercentageMap: Readonly<
    Map<DerivedTeamName, number>
> = Object.freeze(
    // These numbers are hard-coded for a quick win,
    // and are computed using team data by the science team.
    // We may change them into live data in the future.
    new Map([
        [DerivedTeamName.Adapting, 12],
        [DerivedTeamName.Anchoring, 11],
        [DerivedTeamName.Bolstering, 8],
        [DerivedTeamName.Cultivating, 17],
        [DerivedTeamName.Executing, 5],
        [DerivedTeamName.Exploring, 11],
        [DerivedTeamName.Pathfinding, 1],
        [DerivedTeamName.Producing, 8],
        [DerivedTeamName.Stabilizing, 8],
        [DerivedTeamName.CultivatingAndProducing, 5],
        [DerivedTeamName.ExploringAndStabilizing, 3]
    ])
);

export enum TeamIndividualContributionType {
    HighBC = 'highBC',
    HighAB = 'highAB',
    HighCD = 'highCD',
    HighAC = 'highAC',
    HighAD = 'highAD',
    HighBD = 'highBD',
    Adapter = 'adapter',
    HighestA = 'highestA',
    HighestB = 'highestB',
    HighestC = 'highestC',
    HighestD = 'highestD'
}

export enum TeamManagerType {
    HighBC = 'fostering',
    HighAB = 'motivating',
    HighCD = 'inspiring',
    HighAC = 'driving',
    HighAD = 'ambitious',
    HighBD = 'nurturing', // same as HighestBD
    Adapter = 'adapter',
    HighestA = 'exceeding',
    HighestB = 'friendly',
    HighestC = 'supportive',
    HighestD = 'ensuring',
    HighestBC = 'prioritizing',
    HighestCD = 'aligning',
    HighestBD = 'nurturing' // same as HighBD
}

export const AdapterPatternValue = 0.4;

export const TeamTopChoiceObjectiveMap: Readonly<
    Map<StrategyQuadrant, string>
> = Object.freeze(
    // This map captures the top choice standard team objective for each quadrant
    // and is hard-coded for a quick win
    new Map([
        [StrategyQuadrant.Cultivating, '6'],
        [StrategyQuadrant.Exploring, '4'],
        [StrategyQuadrant.Producing, '5'],
        [StrategyQuadrant.Stabilizing, '7']
    ])
);

export const StategyTopChoiceObjectiveMap: Readonly<
    Map<StrategyQuadrant, string>
> = Object.freeze(
    // This map captures the top choice strategy objective for each quadrant
    // and is hard-coded for a quick win
    new Map([
        [StrategyQuadrant.Cultivating, '11'],
        [StrategyQuadrant.Exploring, '10'],
        [StrategyQuadrant.Producing, '17'],
        [StrategyQuadrant.Stabilizing, '7']
    ])
);

// This enum definition needs to match the same enum defined in backend
// under the namespace of PI.Core.Common.Identifiers
export enum DreamTeamFunctionType {
    Unknown = 0,
    AccountingFinance = 1,
    OfficeAdministrative = 2,
    ArtsDesign = 3,
    BusinessDevelopment = 4,
    CommunitySocialServices = 5,
    ComputerProgramming = 6,
    Consulting = 7,
    Education = 8,
    Engineering = 9,
    Executive = 10,
    HealthcareServices = 11,
    HumanResources = 12,
    InformationTechnology = 13,
    Legal = 14,
    Marketing = 15,
    MediaCommunications = 16,
    MilitaryProtectiveServices = 17,
    Operations = 18,
    ProgramProductManagement = 19,
    QualityAssurance = 20,
    ResearchDevelopment = 21,
    Sales = 22,
    SpecialProjects = 23,
    CustomerSupport = 24,
    Other = 99
}

export type DreamTeamFunctionTranslation = {
    teamFunctionType: DreamTeamFunctionType;
    translationKey: string;
};

export type DreamTeamFunctionDisplay = {
    teamFunctionType: DreamTeamFunctionType;
    display: string;
};

export type DreamTeamImprovementTranslation = {
    teamImprovementId: TeamImprovementId;
    translationKey: string;
};

export type DreamTeamImprovementDisplay = {
    teamImprovementId: TeamImprovementId;
    display: string;
};

export const dreamTeamFunctionDisplayInfoList: DreamTeamFunctionTranslation[] =
    [
        {
            teamFunctionType: DreamTeamFunctionType.AccountingFinance,
            translationKey: 'teamConfig.team-function.accounting-finance'
        },
        {
            teamFunctionType: DreamTeamFunctionType.OfficeAdministrative,
            translationKey: 'teamConfig.team-function.office-administrative'
        },
        {
            teamFunctionType: DreamTeamFunctionType.ArtsDesign,
            translationKey: 'teamConfig.team-function.arts-design'
        },
        {
            teamFunctionType: DreamTeamFunctionType.BusinessDevelopment,
            translationKey: 'teamConfig.team-function.business-development'
        },
        {
            teamFunctionType: DreamTeamFunctionType.CommunitySocialServices,
            translationKey: 'teamConfig.team-function.community-social-services'
        },
        {
            teamFunctionType: DreamTeamFunctionType.ComputerProgramming,
            translationKey: 'teamConfig.team-function.computer-programming'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Consulting,
            translationKey: 'teamConfig.team-function.consulting'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Education,
            translationKey: 'teamConfig.team-function.education'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Engineering,
            translationKey: 'teamConfig.team-function.engineering'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Executive,
            translationKey: 'teamConfig.team-function.executive'
        },
        {
            teamFunctionType: DreamTeamFunctionType.HealthcareServices,
            translationKey: 'teamConfig.team-function.healthcare-services'
        },
        {
            teamFunctionType: DreamTeamFunctionType.HumanResources,
            translationKey: 'teamConfig.team-function.human-resources'
        },
        {
            teamFunctionType: DreamTeamFunctionType.InformationTechnology,
            translationKey: 'teamConfig.team-function.information-technology'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Legal,
            translationKey: 'teamConfig.team-function.legal'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Marketing,
            translationKey: 'teamConfig.team-function.marketing'
        },
        {
            teamFunctionType: DreamTeamFunctionType.MediaCommunications,
            translationKey: 'teamConfig.team-function.media-communications'
        },
        {
            teamFunctionType: DreamTeamFunctionType.MilitaryProtectiveServices,
            translationKey:
                'teamConfig.team-function.military-protective-services'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Operations,
            translationKey: 'teamConfig.team-function.operations'
        },
        {
            teamFunctionType: DreamTeamFunctionType.ProgramProductManagement,
            translationKey:
                'teamConfig.team-function.program-product-management'
        },
        {
            teamFunctionType: DreamTeamFunctionType.QualityAssurance,
            translationKey: 'teamConfig.team-function.quality-assurance'
        },
        {
            teamFunctionType: DreamTeamFunctionType.ResearchDevelopment,
            translationKey: 'teamConfig.team-function.researchDevelopment'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Sales,
            translationKey: 'teamConfig.team-function.sales'
        },
        {
            teamFunctionType: DreamTeamFunctionType.SpecialProjects,
            translationKey: 'teamConfig.team-function.special-projects'
        },
        {
            teamFunctionType: DreamTeamFunctionType.CustomerSupport,
            translationKey: 'teamConfig.team-function.customer-support'
        },
        {
            teamFunctionType: DreamTeamFunctionType.Other,
            translationKey: 'text.common-terms.other-specify'
        }
    ];

// Parameterizing this on the TranslationService.key function interface
// so that we don't make TranslationService a dependency of this constants file
export function getSortedDreamTeamFunctionList(trKey: (key: string) => string) {
    const teamFunctionDisplays = dreamTeamFunctionDisplayInfoList.map(
        (func) => ({
            teamFunctionType: func.teamFunctionType,
            display: trKey(func.translationKey)
        })
    );

    // order all items except the "Other" item alphabetically
    teamFunctionDisplays.sort((a, b) => {
        if (a.teamFunctionType === DreamTeamFunctionType.Other) {
            return b.display.localeCompare(b.display);
        } else if (b.teamFunctionType === DreamTeamFunctionType.Other) {
            return a.display.localeCompare(a.display);
        } else {
            return a.display.localeCompare(b.display);
        }
    });

    return teamFunctionDisplays;
}

export type DreamTeamCrossFunctionIdTranslation = {
    crossFunctionId: DreamTeamCrossFunctionId;
    translationKey: string;
};

// This enum definition needs to match the same enum defined in backend
// under the namespace of PI.Core.Business.Strategy.Identifiers
export enum DreamTeamCrossFunctionId {
    Unknown = 0,
    Yes = 1,
    No = 2,
    NotSure = 3
}

export const dreamTeamCrossFunctionIdDisplayInfoList: DreamTeamCrossFunctionIdTranslation[] =
    [
        {
            crossFunctionId: DreamTeamCrossFunctionId.Yes,
            translationKey: 'text.common-terms.yes'
        },
        {
            crossFunctionId: DreamTeamCrossFunctionId.No,
            translationKey: 'text.common-terms.no'
        },
        {
            crossFunctionId: DreamTeamCrossFunctionId.NotSure,
            translationKey: 'text.common-terms.unsure'
        }
    ];

// Parameterizing this on the TranslationService.key function interface
// so that we don't make TranslationService a dependency of this constants file
export function getSortedDreamTeamCrossFunctionList(
    trKey: (key: string) => string
) {
    const teamCrossFunctionIdDisplays =
        dreamTeamCrossFunctionIdDisplayInfoList.map((func) => ({
            crossFunctionId: func.crossFunctionId,
            display: trKey(func.translationKey)
        }));

    // Order the list as "Yes, No, Unsure".
    teamCrossFunctionIdDisplays.sort(
        (a, b) => a.crossFunctionId - b.crossFunctionId
    );
    return teamCrossFunctionIdDisplays;
}
export type DreamTeamCrossFunctionIdDisplay = {
    crossFunctionId: DreamTeamCrossFunctionId;
    display: string;
};

// This enum definition needs to match the same enum defined in backend
// under the namespace of PI.Core.Common.Identifiers
// An enum that defines team improvement ids
export enum TeamImprovementId {
    Unknown = 0,
    Accountability = 1,
    Communication = 2,
    ConflictReduction = 3,
    DecisionMaking = 4,
    InnovationOrCreativity = 5,
    ProcessAlignment = 6,
    Productivity = 7,
    TeamCulture = 8,
    NotSure = 9,
    Other = 10,
    Adaptability = 11,
    AdherenceToProcesses = 12,
    AttentionToDetail = 13,
    ClientCustomerOrientation = 14,
    Collaboration = 15,
    ContinuousImprovement = 16,
    DelegatingOwnership = 17,
    FocusOnCompetitors = 18,
    GoalAlignment = 19,
    ManagingConflict = 20,
    RoleClarity = 21,
    SharingResources = 22,
    TeamEfficiency = 23,
    TeamFocus = 24
}

export const dreamTeamImprovementDisplayInfoList: DreamTeamImprovementTranslation[] =
    [
        {
            teamImprovementId: TeamImprovementId.Accountability,
            translationKey: 'teamConfig.team-improvement.accountability'
        },
        {
            teamImprovementId: TeamImprovementId.Communication,
            translationKey: 'teamConfig.team-improvement.communication'
        },
        {
            teamImprovementId: TeamImprovementId.ConflictReduction,
            translationKey: 'teamConfig.team-improvement.conflict-reduction'
        },
        {
            teamImprovementId: TeamImprovementId.DecisionMaking,
            translationKey: 'teamConfig.team-improvement.decision-making'
        },
        {
            teamImprovementId: TeamImprovementId.InnovationOrCreativity,
            translationKey: 'teamConfig.team-improvement.innovation-creativity'
        },
        {
            teamImprovementId: TeamImprovementId.ProcessAlignment,
            translationKey: 'teamConfig.team-improvement.process-alignment'
        },
        {
            teamImprovementId: TeamImprovementId.Productivity,
            translationKey: 'teamConfig.team-improvement.productivity'
        },
        {
            teamImprovementId: TeamImprovementId.TeamCulture,
            translationKey: 'teamConfig.team-improvement.team-culture'
        },
        {
            teamImprovementId: TeamImprovementId.NotSure,
            translationKey: 'text.common-terms.unsure'
        },
        {
            teamImprovementId: TeamImprovementId.Adaptability,
            translationKey: 'teamConfig.team-improvement.adaptability'
        },
        {
            teamImprovementId: TeamImprovementId.AdherenceToProcesses,
            translationKey: 'teamConfig.team-improvement.process-adherence'
        },
        {
            teamImprovementId: TeamImprovementId.AttentionToDetail,
            translationKey: 'teamConfig.team-improvement.attention-to-detail'
        },
        {
            teamImprovementId: TeamImprovementId.ClientCustomerOrientation,
            translationKey: 'teamConfig.team-improvement.customer-orientation'
        },
        {
            teamImprovementId: TeamImprovementId.Collaboration,
            translationKey: 'teamConfig.team-improvement.collaboration'
        },
        {
            teamImprovementId: TeamImprovementId.ContinuousImprovement,
            translationKey: 'teamConfig.team-improvement.continuous-improvement'
        },
        {
            teamImprovementId: TeamImprovementId.DelegatingOwnership,
            translationKey: 'teamConfig.team-improvement.ownership-delegation'
        },
        {
            teamImprovementId: TeamImprovementId.FocusOnCompetitors,
            translationKey: 'teamConfig.team-improvement.competitor-focus'
        },
        {
            teamImprovementId: TeamImprovementId.GoalAlignment,
            translationKey: 'teamConfig.team-improvement.goal-alignment'
        },
        {
            teamImprovementId: TeamImprovementId.ManagingConflict,
            translationKey: 'teamConfig.team-improvement.conflict-management'
        },
        {
            teamImprovementId: TeamImprovementId.RoleClarity,
            translationKey: 'teamConfig.team-improvement.role-clarity'
        },
        {
            teamImprovementId: TeamImprovementId.SharingResources,
            translationKey: 'teamConfig.team-improvement.resource-sharing'
        },
        {
            teamImprovementId: TeamImprovementId.TeamEfficiency,
            translationKey: 'teamConfig.team-improvement.team-efficiency'
        },
        {
            teamImprovementId: TeamImprovementId.TeamFocus,
            translationKey: 'teamConfig.team-improvement.team-focus'
        },
        // "Other" should remain at the bottom for getSortedDreamTeamImprovementDisplayInfoList() to work correctly
        {
            teamImprovementId: TeamImprovementId.Other,
            translationKey: 'text.common-terms.other-specify'
        }
    ];

// Parameterizing this on the TranslationService.key function interface
// so that we don't make TranslationService a dependency of this constants file
export function getSortedDreamTeamImprovementDisplayInfoList(
    trKey: (key: string) => string
) {
    const teamImprovementDisplays = dreamTeamImprovementDisplayInfoList.map(
        (improvement) => ({
            teamImprovementId: improvement.teamImprovementId,
            display: trKey(improvement.translationKey)
        })
    );

    // order all items except the "Other" item alphabetically
    teamImprovementDisplays.sort((a, b) => {
        if (a.teamImprovementId === TeamImprovementId.Other) {
            return b.display.localeCompare(b.display);
        } else if (b.teamImprovementId === TeamImprovementId.Other) {
            return a.display.localeCompare(a.display);
        } else {
            return a.display.localeCompare(b.display);
        }
    });
    return teamImprovementDisplays;
}

export const teamFunctionStandardObjectiveMap: Readonly<
    Map<DreamTeamFunctionType, Readonly<Map<StrategyQuadrant, string[]>>>
> = Object.freeze(
    // This map captures the top choice standard team objective for each quadrant
    // and is hard-coded for a quick win
    new Map([
        [
            DreamTeamFunctionType.AccountingFinance,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, ['4']],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.ArtsDesign,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, ['4', '19']],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, ['13']]
            ])
        ],
        [
            DreamTeamFunctionType.BusinessDevelopment,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['12', '19']],
                [StrategyQuadrant.Producing, ['15', '3']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.CommunitySocialServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['11', '6']],
                [StrategyQuadrant.Exploring, ['1']],
                [StrategyQuadrant.Producing, ['14']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.ComputerProgramming,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '9', '18']]
            ])
        ],
        [
            DreamTeamFunctionType.Consulting,
            new Map([
                [StrategyQuadrant.Cultivating, ['8', '16']],
                [StrategyQuadrant.Exploring, ['19']],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.CustomerSupport,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '8']],
                [StrategyQuadrant.Exploring, ['19', '12']],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.Education,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '8']],
                [StrategyQuadrant.Exploring, ['4']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Engineering,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, ['10']],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.Executive,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['10', '19']],
                [StrategyQuadrant.Producing, ['3', '15']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.HealthcareServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['8', '20']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.HumanResources,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '11', '8']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.InformationTechnology,
            new Map([
                [StrategyQuadrant.Cultivating, ['8', '6']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Legal,
            new Map([
                [StrategyQuadrant.Cultivating, ['8']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5', '14']],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.Marketing,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['19', '4', '1']],
                [StrategyQuadrant.Producing, ['15', '3']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.MediaCommunications,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['19', '4']],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.MilitaryProtectiveServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['2', '7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.OfficeAdministrative,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '8']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '2']]
            ])
        ],
        [
            DreamTeamFunctionType.Operations,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '8']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['7', '9', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.ProgramProductManagement,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['19']],
                [StrategyQuadrant.Producing, ['17']],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.QualityAssurance,
            new Map([
                [StrategyQuadrant.Cultivating, ['8']],
                [StrategyQuadrant.Exploring, ['1']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '9', '7']]
            ])
        ],
        [
            DreamTeamFunctionType.ResearchDevelopment,
            new Map([
                [StrategyQuadrant.Cultivating, ['8']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['5', '14', '17']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.Sales,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['12', '19', '1', '4']],
                [StrategyQuadrant.Producing, ['3']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.SpecialProjects,
            new Map([
                [StrategyQuadrant.Cultivating, ['6']],
                [StrategyQuadrant.Exploring, ['10']],
                [StrategyQuadrant.Producing, ['5']],
                [StrategyQuadrant.Stabilizing, ['7', '18']]
            ])
        ]
    ])
);

export const teamFunctionLeadershipObjectiveMap: Readonly<
    Map<DreamTeamFunctionType, Readonly<Map<StrategyQuadrant, string[]>>>
> = Object.freeze(
    // This map captures the top choice standard team objective for each quadrant
    // and is hard-coded for a quick win
    new Map([
        [
            DreamTeamFunctionType.AccountingFinance,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, ['7', '9', '13', '18']]
            ])
        ],
        [
            DreamTeamFunctionType.ArtsDesign,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['1', '4', '10']],
                [StrategyQuadrant.Producing, ['14']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.BusinessDevelopment,
            new Map([
                [StrategyQuadrant.Cultivating, ['8', '16']],
                [StrategyQuadrant.Exploring, ['1']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['7', '18']]
            ])
        ],
        [
            DreamTeamFunctionType.CommunitySocialServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['8']],
                [StrategyQuadrant.Exploring, ['10', '12']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['7', '9']]
            ])
        ],
        [
            DreamTeamFunctionType.ComputerProgramming,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '16']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, ['2', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Consulting,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['12']],
                [StrategyQuadrant.Producing, ['5', '17']],
                [StrategyQuadrant.Stabilizing, ['2']]
            ])
        ],
        [
            DreamTeamFunctionType.CustomerSupport,
            new Map([
                [StrategyQuadrant.Cultivating, ['11', '20']],
                [StrategyQuadrant.Exploring, ['1']],
                [StrategyQuadrant.Producing, ['17']],
                [StrategyQuadrant.Stabilizing, ['13']]
            ])
        ],
        [
            DreamTeamFunctionType.Education,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '20']],
                [StrategyQuadrant.Exploring, ['1', '4', '19']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.Engineering,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '11']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['14']],
                [StrategyQuadrant.Stabilizing, ['7', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Executive,
            new Map([
                [StrategyQuadrant.Cultivating, ['11', '16']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['3', '17']],
                [StrategyQuadrant.Stabilizing, ['7']]
            ])
        ],
        [
            DreamTeamFunctionType.HealthcareServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['4', '10', '12']],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.HumanResources,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '11', '16']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '7']]
            ])
        ],
        [
            DreamTeamFunctionType.InformationTechnology,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['12']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '7', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Legal,
            new Map([
                [StrategyQuadrant.Cultivating, ['11', '20']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '7', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.Marketing,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, ['10', '4']],
                [StrategyQuadrant.Producing, ['17', '3']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.MediaCommunications,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['10', '1']],
                [StrategyQuadrant.Producing, ['14', '17']],
                [StrategyQuadrant.Stabilizing, ['9']]
            ])
        ],
        [
            DreamTeamFunctionType.MilitaryProtectiveServices,
            new Map([
                [StrategyQuadrant.Cultivating, ['16', '20']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, ['14']],
                [StrategyQuadrant.Stabilizing, ['9', '18']]
            ])
        ],
        [
            DreamTeamFunctionType.OfficeAdministrative,
            new Map([
                [StrategyQuadrant.Cultivating, ['6', '8']],
                [StrategyQuadrant.Exploring, ['19']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['7', '2']]
            ])
        ],
        [
            DreamTeamFunctionType.Operations,
            new Map([
                [StrategyQuadrant.Cultivating, ['16']],
                [StrategyQuadrant.Exploring, []],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['7', '9', '13', '18']]
            ])
        ],
        [
            DreamTeamFunctionType.ProgramProductManagement,
            new Map([
                [StrategyQuadrant.Cultivating, []],
                [StrategyQuadrant.Exploring, ['4', '12']],
                [StrategyQuadrant.Producing, ['14', '15']],
                [StrategyQuadrant.Stabilizing, ['18']]
            ])
        ],
        [
            DreamTeamFunctionType.QualityAssurance,
            new Map([
                [StrategyQuadrant.Cultivating, ['20']],
                [StrategyQuadrant.Exploring, ['10']],
                [StrategyQuadrant.Producing, ['15']],
                [StrategyQuadrant.Stabilizing, ['9', '13']]
            ])
        ],
        [
            DreamTeamFunctionType.ResearchDevelopment,
            new Map([
                [StrategyQuadrant.Cultivating, ['8']],
                [StrategyQuadrant.Exploring, ['4', '12']],
                [StrategyQuadrant.Producing, ['15', '14']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.Sales,
            new Map([
                [StrategyQuadrant.Cultivating, ['11', '16']],
                [StrategyQuadrant.Exploring, ['10']],
                [StrategyQuadrant.Producing, ['3', '17']],
                [StrategyQuadrant.Stabilizing, []]
            ])
        ],
        [
            DreamTeamFunctionType.SpecialProjects,
            new Map([
                [StrategyQuadrant.Cultivating, ['8', '11']],
                [StrategyQuadrant.Exploring, ['10']],
                [StrategyQuadrant.Producing, []],
                [StrategyQuadrant.Stabilizing, ['2', '13']]
            ])
        ]
    ])
);

export type DreamTeamAction = {
    actionId: number;
    isDone: boolean;
    assigneeId: string;
    dueDate: Date;
    isEmailReminderEnabled: boolean;
    reminderDate: Date;
    completedDate: Date;
    rating: DreamTeamActionRating;
};

export enum DreamTeamActionRating {
    Unknown = 0,
    NotHelpful = 1,
    SomewhatHelpful = 2,
    Helpful = 3,
    VeryHelpful = 4,
    ExtremelyHelpful = 5
}

export type CustomAction = {
    description: string;
    isDone: boolean;
    assigneeId: string;
    dueDate: Date;
    isEmailReminderEnabled: boolean;
    reminderDate: Date;
    completedDate: Date;
};

export const defaultGuid = '00000000-0000-0000-0000-000000000000';
export const z = 'z'; // used to sort people with no reference profiles
export const actionsPerPeekPanelPage = 6;

export const actionRatingDefinitions: string[] = [
    'teamDiscovery.actionratings.nothelpful',
    'teamDiscovery.actionratings.somewhathelpful',
    'teamDiscovery.actionratings.helpful',
    'teamDiscovery.actionratings.veryhelpful',
    'teamDiscovery.actionratings.extremelyhelpful'
];

export const maxCustomActionsCount = 10;
export const maxCharactersPerCustomAction = 250;

export enum AlignmentResponseType {
    Unknown = 0,
    Team = 1,
    Person = 2
}

export enum AlignmentResponseValue {
    Unknown = 0,
    Agree = 1,
    Disagree = 2
}
